import { useRef, useEffect, useContext } from 'react';
import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax
import tippy from 'tippy.js';

import { mapContext } from '../Services/MapContext';
import { isMobile, isDevMode, constructPopupContent } from '../Utilities/Helpers';

import {
	DEFAULT_COORDS,
	DEFAULT_ZOOM,
	MAPBOX_TOKEN,
	LAYERS,
	LAYER_TYPE_COLUMN,
	DEFAULT_BASEMAP_STYLE
} from '../Utilities/Config';

import 'mapbox-gl/dist/mapbox-gl.css';
import '../Styles/MapMapbox.css';

const Map = (props) => {
	const {
		isEmbedded,
		layersVisible,
		selectedData,
		setSelectedData,
		toggleSidebar,
		sidebarWidth,
		setLoaderVisible,
		onMouseMove,
		setTooltipContent,
		setHoverTooltipVisible,
		language,
		children,
		iframeMode
	} = props;

	mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_KEY || MAPBOX_TOKEN;

	const {
		mapContainer,
		map,
		setMap,
		mapLoaded,
		setMapLoaded,
		layersLoaded,
		setLayersLoaded,
		popupData, setPopupData
	} = useContext(mapContext);

	const selectedDataPrev = useRef(null);
	const divMarkerStorage = useRef({});
	const layersZoomVisibility = useRef([]);
	const tooltipPrevId = useRef(null);
	const tooltipPrevProps = useRef(null);
	const addedMapImage = useRef([]);
	const highlightedFeature = useRef(null);
	const highlightedFeaturePrev = useRef(null);

	let sidebarWidthLocal = useRef(sidebarWidth || 0);

	const showTooltip = (type, content) => {
		setTooltipContent({type: type, content: content});
	}

	const mouseLayerMove = (props, layerName) => {
		const tooltipLayerName = `${layerName}-${props.fid}`;
		const tooltipSame = (tooltipPrevId.current === `${tooltipLayerName}`);
		if (!tooltipSame) {
			showTooltip(layerName, props);
		} else {
			showTooltip(layerName, tooltipPrevProps.current)
		}
		tooltipPrevId.current = tooltipLayerName;
		tooltipPrevProps.current = props;
	}
	
	const toggleLayers = (layerIDs) => {
		if (!map) return;
		Object.entries(layerIDs).forEach((data) => {
			const layerId = data[0];
			const layerParams = data[1];
			const currentZoom = map.getZoom();
			const { zoomVisibility } = layerParams;			
			const state = zoomVisibility ? (
					currentZoom >= zoomVisibility.min &&
					currentZoom < zoomVisibility.max &&
					layerParams.state
				) : layerParams.state;

			changeLayerVisibility(layerId, state);

			// and additional layers
			if (layerParams.additionalLayers) {
				layerParams.additionalLayers.forEach(additionalLayer => {
					changeLayerVisibility(additionalLayer.id, state);
				});
			}

			// check if there is an additional outline layer
			let outlineLayerName = `${layerId}_outline`;
			if (map.getLayer(outlineLayerName)) {
				changeLayerVisibility(outlineLayerName, state);
			}
		});
	}

	// set layer visibility depending on the zoom level (if configured)
	const setLayersZoomVisibility = (map, currentZoomLevel) => {
		layersZoomVisibility.current.forEach((layer) => {
			const layerZoomParams = layer.zoomParams;
			const realVisibilityState = layersVisible.current[layer.id]?.state;
			const layerVisible = (currentZoomLevel >= layerZoomParams.min && currentZoomLevel <= layerZoomParams.max) && realVisibilityState;
			const outlineLayerName = `${layer.id}_outline`;

			let opacityType = '';
			let layerOpacity = 1;
			let outlineOpacity = 0.2;
			switch (layer.layerType) {
				case 'circle':
					opacityType = 'circle';
					layerOpacity = 1;
					break;
				case 'symbol':
					opacityType = 'icon';
					layerOpacity = 1;
					break;
				case 'line':
					opacityType = 'line';
					layerOpacity = 1;
					break;
				default:
					opacityType = 'fill';
					break;	
			}

			if (map.getLayer(layer.id)) {
				map.setLayoutProperty(layer.id, 'visibility', layerVisible ? 'visible' : 'none');
				map.setPaintProperty(layer.id, `${opacityType}-opacity`, layerVisible ? layerOpacity : 0);
			}
			if (map.getLayer(outlineLayerName)) {
				map.setLayoutProperty(outlineLayerName, 'visibility', layerVisible ? 'visible' : 'none');
				map.setPaintProperty(outlineLayerName, `line-opacity`, layerVisible ? outlineOpacity : 0);
			}
		})
	}


	// change layer visibility
	// change to something else, this is ridiculous!
	const changeLayerVisibility = (layerName, state) => {
		if (map.getLayer(layerName)) {
			// hotfix for layers controled from Filter component
			const layerParams = layersVisible.current[layerName];
			if (layerParams?.controlAsLayer) {
				map.setPaintProperty(layerName, `icon-opacity`, state ? 1 : 0);
			}

			// for all layers
			map.setLayoutProperty(layerName, 'visibility', state ? 'visible' : 'none');
		}

		if (map.getLayer(`${layerName}-points`)) {
			map.setLayoutProperty(`${layerName}-points`, 'visibility', state ? 'visible' : 'none');
		}

		// especially this part!
		if (map.getLayer(`${layerName}_outline`)) {
			map.setLayoutProperty(`${layerName}_outline`, 'visibility', state ? 'visible' : 'none');
		}
		if (map.getLayer(`${layerName}_outline_0`)) {
			map.setLayoutProperty(`${layerName}_outline_0`, 'visibility', state ? 'visible' : 'none');
			//setLayerOpacity(`${layerName}_outline_0`, opacity);
		}
		if (map.getLayer(`${layerName}_outline_1`)) {
			map.setLayoutProperty(`${layerName}_outline_1`, 'visibility', state ? 'visible' : 'none');
			//setLayerOpacity(`${layerName}_outline_1`, opacity);
		}

		// html div markers
		if (!LAYERS[layerName]?.type === 'divMarker') return;
		if (!divMarkerStorage.current[layerName]) return;
		divMarkerStorage.current[layerName].forEach((marker) => {
			if (state) {
				marker.addTo(map);
			} else {
				marker.remove();
			}
		});
	}

	// add mbtiles layer
	const addMBTilesLayer = async (layerId, parentProperties = null) => {
		await new Promise(async (resolve, reject) => {
			let props = layersVisible.current[layerId];
			const srcName = `${layerId}_src`;
			const isRaster = (props?.layerType === 'raster');

			// if it's an additional layer
			if (!props && parentProperties?.additionalLayers) {
				let foundLayers = parentProperties.additionalLayers.filter(el => el.id === layerId);
				props = foundLayers.length ? foundLayers[0] : null;
			}

			// build default url
			/*
			let tileLayerId = null;
			if (props.currentYear && props.currentType) {
				tileLayerId = getTileLayerId({
					pattern: props.pattern,
					year: props.currentYear,
					type: props.currentType
				});
			} else {
				tileLayerId = props.tilesId;
			}
			*/

			let layerVisibility = props.state;
			if (props.zoomVisibility) {
				const zoomParam = props.zoomVisibility;
				const currentZoom = map.getZoom();
				layerVisibility = (currentZoom >= zoomParam.min && currentZoom < zoomParam.max) && props.state;
			}

			const tileLayerId = props.tilesId;
			const url = `${props.tileserver}/${tileLayerId}/{z}/{x}/{y}.pbf${isRaster ? '?raster=true' : ''}`;

			if (!map.getSource(srcName)) {
				map.addSource(srcName, {
					'type': isRaster ? 'raster' : 'vector',
					'tiles': [url],
					'promoteId': 'fid',
					...props.zoom,
				});
			}

			// load pattern images if needed
			if (props.pattern) {
				await new Promise((resolve, reject) => {
					if (addedMapImage.current.includes(props.pattern.name)) {
						resolve();
					} else {
						addedMapImage.current.push(props.pattern.name)
						map.loadImage(props.pattern.image, (err, image) => {
							if (err) console.log(err);
							map.addImage(props.pattern.name, image);
							resolve();
						});
					}
				});
			}

			if (props.iconImage) {
				await new Promise((resolve, reject) => {
					map.loadImage(props.iconImage, (err, image) => {
						if (err) throw err;
						map.addImage(`${layerId}_icon`, image);
						resolve();
					});
				});
			}

			// image patterns
			let paintProps = props.currentType ? props.paint[props.currentType] : props.paint;
			/*
			if (props.pattern) {

				paintProps = {
					'fill-pattern': [
						'case',
						props.pattern.filterExpression,	
						props.pattern.name,
						true
					]
				}

				paintProps = { ...paintProps, ...props.paint };
			}
			*/

			// (re-)add the layer
			if (map.getLayer(layerId)) map.removeLayer(layerId);
			let properties = {
				'id': layerId,
				'type': props.layerType,
				'source': srcName,
				'source-layer': tileLayerId,
			};

			// apply custom styling
			if (paintProps) properties['paint'] = paintProps;
			if (props.layout) properties['layout'] = { ...props.layout };
			if (props.iconImage) properties['layout']['icon-image'] = `${layerId}_icon`;

			// generate conditional color properties
			if (props.customPaint) {
				let conditionalPaintArray = Object.entries(props.legendConfig).map(el => {
					return {...el[1], name: el[0]}
				}).map(el => [el.name, el.color]);
				
				conditionalPaintArray.push('transparent');

				properties['paint'] = {
					...properties['paint'],
					[`${props.layerType}-color`]: [
						'match',
						['get', props.matchPaintProperty],
						...conditionalPaintArray.flat()
					]
				}
			}

			// set default filter expression
			if (props?.defaultFilterExpression) {
				properties.filter = props?.defaultFilterExpression;
			}

			map.addLayer(properties);
			if (props.beforeLayer && map.getLayer(props.beforeLayer)) {
				map.moveLayer(layerId, props.beforeLayer);
			}

			// add an outline around the polygon
			if (props.outlineColor) {
				const outlineLayerName = `${layerId}_outline`;
				if (map.getLayer(outlineLayerName)) map.removeLayer(outlineLayerName);
				map.addLayer({
					'id': outlineLayerName,
					'type': 'line',
					'source': srcName,
					'source-layer': tileLayerId,
					'layout': {},
					'paint': {
						'line-color': props.outlineColor,
						'line-width': props.outlineWidth ? props.outlineWidth : 3,
						'line-opacity': props.outlineOpacity ? props.outlineOpacity : 1
					}
				});
				if (props.beforeLayer && map.getLayer(props.beforeLayer)) {
					map.moveLayer(outlineLayerName, props.beforeLayer);
				}
				changeLayerVisibility(outlineLayerName, layerVisibility);
			}

			// add an outline around different patterns
			/*
			if (props.patternsArray) {
				props.patternsArray.forEach((p, i) => {
					map.addLayer({
						'id': `${layerId}_outline_${i}`,
						'type': 'line',
						'filter': ['==', ['get', 'type'], p.param],
						'source': srcName,
						'source-layer': tileLayerId,
						'layout': {},
						'paint': {
							'line-color': p.outline,
							'line-width': props.outlineWidth ? props.outlineWidth : 3
						}
					});
					if (props.beforeLayer && map.getLayer(props.beforeLayer)) {
						map.moveLayer(`${layerId}_outline_${i}`, props.beforeLayer);
					}
					changeLayerVisibility(`${layerId}_outline_${i}`, layerVisibility);
				});
			}
			*/

			// Add extra layers if needed
			if (props.additionalLayers) {
				props.additionalLayers.forEach(async (layer) => {
					await addLayer(layer.id, null, props);
				});
			}

			// Interactivity
			// Hover over the marker - pointer
			const additionalLayersArray = props?.additionalLayers?.map(el => el?.id) || [];
			if (props.interactive) {
				const layersToInteractWith = [layerId, ...additionalLayersArray];
				map.on('mousemove', layersToInteractWith, (e) => {
					if (e.features.length === 0) return;
					const feature = e.features[0];

					// (de)highlight feature
					deHighlightFeature(map);
					highlightedFeaturePrev.current = highlightedFeature.current;
					highlightedFeature.current = feature;
					map.setFeatureState(highlightedFeature.current, { selected: true });

					onMouseMove({left: e.point.x, top: e.point.y});
					mouseLayerMove(feature.properties, feature.layer.id);
					map.getCanvas().style.cursor = 'pointer';
				});
	
				// Mouse left the marker
				map.on('mouseleave', layersToInteractWith, (e) => {
					map.getCanvas().style.cursor = '';
					showTooltip(null, null);
				});
	
				// On marker click - show popup with data
				map.on('click', layersToInteractWith, (e) => {
					const features = map.queryRenderedFeatures(e.point);
					const interactiveFeatures = features.filter(el => {
						return layersToInteractWith.includes(el.layer.id);
					});
					if (interactiveFeatures.length) {
						const topFeature = interactiveFeatures[0];
						if (isMobile()) {
							setSelectedData(topFeature);
						} else {
							setPopupData({
								id: topFeature.layer.id,
								coordinates: e.lngLat,
								feature: topFeature?.properties
							});
						}
					}
				});
			}

			changeLayerVisibility(layerId, layerVisibility);
			resolve();
		});
	}

	// remove feature highlighting
	const deHighlightFeature = (mapRef) => {
		if (!highlightedFeature.current) return;
		[highlightedFeature.current, highlightedFeaturePrev?.current].filter(el => el).forEach((el) => {
			mapRef?.setFeatureState(el, { selected: false });
		});
	}

	// add raster layer
	const addRasterTilesLayer = async (layerId) => {
		const srcName = `${layerId}_src`;
		const props = layersVisible.current[layerId];

		const url = props.wms ? `${props.url}&LAYERS=${Object.keys(props.legendConfig).join(',')}` : props.url;

		map.addSource(srcName, {
			'type': 'raster',
			'scheme': props.wms ? 'tms': 'xyz',
			'tiles': [ url ],
			...props.zoom
		});
		
		map.addLayer({
			'id': layerId,
			'type': 'raster',
			'source': srcName,
			'paint': {},
		});

		changeLayerVisibility(layerId, props.state);
	}

	// add geojson layers
	const addLayer = async (layerId, parentProperties = null) => {
		let props = LAYERS[layerId];
		let layerType = props?.type;

		if (props.zoomVisibility) {
			layersZoomVisibility.current.push({
				...props,
				id: layerId,
				zoomParams: props.zoomVisibility
			});
		}

		// if it's an additional layer
		if (parentProperties && parentProperties?.additionalLayers?.length) {
			props = parentProperties;
			let foundLayers = props.additionalLayers.filter(el => el.id === layerId);
			layerType = foundLayers.length ? foundLayers[0].type : null;
		}

		if (layerType === 'vtiles') {
			await addMBTilesLayer(layerId, parentProperties);
		} else if (layerType === 'raster') {
			addRasterTilesLayer(layerId);
		} else { }
	}

	// click on marker event
	useEffect(() => {
		if (mapLoaded && layersLoaded) {
			clickOnFeature(selectedData);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mapLoaded, layersLoaded, selectedData]);

	const clickOnFeature = (feature) => {
		if (!feature) return;
		// Open sidebar
		toggleSidebar(true);
	}

	// Init map
	useEffect(() => {
		const initializeMap = ({ setMap, mapContainer }) => {
			if (!language) return;

			const scrollCtrlMsg = language?.['cooperative_gestures:ScrollZoomBlocker:CtrlMessage'] ?? "Brug ctrl + scroll for at zoome på kortet";
			const scrollCmdMsg = language?.['cooperative_gestures:ScrollZoomBlocker:CmdMessage'] ?? "Brug ⌘ + scroll for at zoome på kortet";
			const touchPanMsg = language?.['cooperative_gestures:TouchPanBlocker:Message'] ?? "Brug to fingre til at flytte kortet";

			const map = new mapboxgl.Map({
				container: mapContainer.current,
				style: `${DEFAULT_BASEMAP_STYLE}${isDevMode() ? '/draft' : ''}`,
				center: DEFAULT_COORDS,
				zoom: DEFAULT_ZOOM,
				attributionControl: false,
				detectRetina: true,
				noWrap: true,
				continuousWorld: false,
				cooperativeGestures: iframeMode,
				locale: {
					'ScrollZoomBlocker.CtrlMessage': scrollCtrlMsg,
					'ScrollZoomBlocker.CmdMessage': scrollCmdMsg,
					'TouchPanBlocker.Message': touchPanMsg,
				},
			});
			setMap(map);

			// disable map rotation using right click + drag
			map.dragRotate.disable();
			// disable map rotation using touch rotation gesture
			map.touchZoomRotate.disableRotation();

			// add custom attribution
			map.addControl(new mapboxgl.AttributionControl({
				compact: false,
				customAttribution: isEmbedded ? `<a href="https://maps.greenpeace.org/maps/gpdk/know-your-nitrate" class="fullscreen" target="_blank" rel="noopener noreferrer">Fullscreen <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><g clipPath="url(#clip0_5659_466)"><path d="M12 12.6667H4C3.63333 12.6667 3.33333 12.3667 3.33333 12V4C3.33333 3.63333 3.63333 3.33333 4 3.33333H7.33333C7.7 3.33333 8 3.03333 8 2.66667C8 2.3 7.7 2 7.33333 2H3.33333C2.59333 2 2 2.6 2 3.33333V12.6667C2 13.4 2.6 14 3.33333 14H12.6667C13.4 14 14 13.4 14 12.6667V8.66667C14 8.3 13.7 8 13.3333 8C12.9667 8 12.6667 8.3 12.6667 8.66667V12C12.6667 12.3667 12.3667 12.6667 12 12.6667ZM9.33333 2.66667C9.33333 3.03333 9.63333 3.33333 10 3.33333H11.7267L5.64 9.42C5.38 9.68 5.38 10.1 5.64 10.36C5.9 10.62 6.32 10.62 6.58 10.36L12.6667 4.27333V6C12.6667 6.36667 12.9667 6.66667 13.3333 6.66667C13.7 6.66667 14 6.36667 14 6V2.66667C14 2.3 13.7 2 13.3333 2H10C9.63333 2 9.33333 2.3 9.33333 2.66667Z" fill="#000"></path></g><defs><clipPath id="clip0_5659_466"><rect width="16" height="16" fill="white"></rect></clipPath></defs></svg></a>` : ''
			}), 'bottom-right');

			// change layer visibility on zoom
			map.on('zoom', (e) => {
				const currentZoom = e.target?.transform?._zoom || 0;
				setLayersZoomVisibility(map, currentZoom);
			});

			// Events to hide tooltip on screen drag
			map.on('touchmove', () => {
				setHoverTooltipVisible(false);
			});
			map.on('dragstart', () => {
				setHoverTooltipVisible(false);
			});
		};

		if (!map) initializeMap({ setMap, mapContainer });
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [setMap, map]);


	// show popup on point click
	useEffect(() => {
		if (!popupData) return;
		const content = constructPopupContent({
			data: popupData,
			language,
			toString: true
		});

		new mapboxgl.Popup()
			.setLngLat(popupData.coordinates)
			.setHTML(content)
			.addTo(map);

		// init tippy.js
		[...document.querySelectorAll('.nitrate-health-icon-wrapper')].forEach(el => { tippy(el) });

		// hide tooltip
		setHoverTooltipVisible(false);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [map, popupData]);


	// Trigger on map load
	useEffect(() => {
		if (map) {
			map.on('load', () => { setMapLoaded(true); });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [map]);


	// fitbounds load data and create layers
	useEffect(() => {
		if (!mapLoaded) return;

		map.fitBounds([
			[7.866211,54.737308],
			[15.249023,57.792089]
		], { padding: 60 });

		Object.keys(LAYERS).forEach(async (layerId) => {
			await addLayer(layerId);
		});
		
		setLoaderVisible(false);
		setLayersLoaded(true);
		toggleLayers(layersVisible.current);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mapLoaded]);


	// Adding event listener on change of layersEnabled
	useEffect(() => {
		toggleLayers(layersVisible.current);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [layersVisible.current]);


	// Update sidebar width
	useEffect(() => {
		sidebarWidthLocal.current = sidebarWidth;
	}, [sidebarWidth]);


	// Trigger on current ID change
	useEffect(() => {
		// Change marker styling
		if (layersLoaded) {

			// Remove style from previous marker
			if (selectedDataPrev && selectedDataPrev.current !== null) {
				const prevSrcName = `${selectedDataPrev.current.properties[LAYER_TYPE_COLUMN]}`;
				if (map.getSource(prevSrcName)) {
					map.setFeatureState({
						source: prevSrcName,
						id: selectedDataPrev.current.id
					}, { 'clicked': false });
				}
			}

			// Add style to the current marker
			if (selectedData && selectedData.current !== null) {
				const srcName = `${selectedData.properties[LAYER_TYPE_COLUMN]}`;
				if (map.getSource(srcName)) {
					map.setFeatureState({
						source: srcName,
						id: selectedData.id
					}, { 'clicked': true });
				}
			}
		}
		selectedDataPrev.current = selectedData;
	}, [layersLoaded, selectedData, map]);

	// Render
	return (
		<div className={`map-wrapper ${isEmbedded ? 'embed' : ''}`}>
			{children}
			<div ref={mapContainer} className="map-container"></div>
		</div>
	);
};

export default Map;